import type { ApiError } from '@/shared/api';

import { AppLocale, translate, useLocale } from '../../../i18n';
import { apiErrorMessages } from './api-error-messages';

type ApiErrorResponse = {
  code?: string;
  params?: unknown;
};

const isApiErrorCode = (
  messages: (typeof apiErrorMessages)[AppLocale.RU],
  code: string,
): code is ApiError['code'] => code in messages;

const isApiErrorParams = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

const formatApiErrorParameter = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return translate(value ? 'common.yes' : 'common.no');
  }

  return JSON.stringify(value);
};

const formatApiErrorMessage = (template: string, params: Record<string, unknown>): string =>
  template.replaceAll(/\{(?<name>[^{}]+)\}/g, (placeholder: string, name: string): string => {
    const value: unknown = params[name];
    return value === undefined ? placeholder : formatApiErrorParameter(value);
  });

const isApiErrorResponse = (value: unknown): value is ApiErrorResponse =>
  value !== null && typeof value === 'object';

const getApiErrorMessage = (error: unknown): string => {
  const { locale } = useLocale();
  const messages = apiErrorMessages[locale.value] ?? apiErrorMessages[AppLocale.RU];

  if (
    !isApiErrorResponse(error) ||
    error.code === undefined ||
    !isApiErrorCode(messages, error.code)
  ) {
    return messages.fallback;
  }

  const params: Record<string, unknown> = isApiErrorParams(error.params) ? error.params : {};
  return formatApiErrorMessage(messages[error.code], params);
};

export { getApiErrorMessage };
